import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Image, MessageSquare, Upload, Star, Clock, CheckCircle, XCircle } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface ProviderContentSubmissionsProps {
  locationId: string;
}

export const ProviderContentSubmissions = ({ locationId }: ProviderContentSubmissionsProps) => {
  const [beforeImage, setBeforeImage] = useState<File | null>(null);
  const [afterImage, setAfterImage] = useState<File | null>(null);
  const [baDescription, setBaDescription] = useState("");
  const [baTreatmentType, setBaTreatmentType] = useState("");
  const [isUploadingBA, setIsUploadingBA] = useState(false);

  const [clientName, setClientName] = useState("");
  const [testimonialText, setTestimonialText] = useState("");
  const [rating, setRating] = useState("5");
  const [testTreatmentType, setTestTreatmentType] = useState("");
  const [isSubmittingTestimonial, setIsSubmittingTestimonial] = useState(false);

  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch existing submissions
  const { data: baSubmissions = [] } = useQuery({
    queryKey: ['provider-ba-submissions', locationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('provider_before_after_submissions')
        .select('*')
        .eq('location_id', locationId)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!locationId,
  });

  const { data: testimonialSubmissions = [] } = useQuery({
    queryKey: ['provider-testimonial-submissions', locationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('provider_testimonial_submissions')
        .select('*')
        .eq('location_id', locationId)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!locationId,
  });

  const handleBeforeAfterSubmit = async () => {
    if (!beforeImage || !afterImage) {
      toast({
        title: "Images Required",
        description: "Please upload both before and after images.",
        variant: "destructive",
      });
      return;
    }

    setIsUploadingBA(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Upload before image
      const beforeExt = beforeImage.name.split('.').pop();
      const beforePath = `${locationId}/${Date.now()}-before.${beforeExt}`;
      const { error: beforeUploadError } = await supabase.storage
        .from('provider-content')
        .upload(beforePath, beforeImage);

      if (beforeUploadError) throw beforeUploadError;

      // Upload after image
      const afterExt = afterImage.name.split('.').pop();
      const afterPath = `${locationId}/${Date.now()}-after.${afterExt}`;
      const { error: afterUploadError } = await supabase.storage
        .from('provider-content')
        .upload(afterPath, afterImage);

      if (afterUploadError) throw afterUploadError;

      // Get public URLs
      const { data: { publicUrl: beforeUrl } } = supabase.storage
        .from('provider-content')
        .getPublicUrl(beforePath);

      const { data: { publicUrl: afterUrl } } = supabase.storage
        .from('provider-content')
        .getPublicUrl(afterPath);

      // Create submission
      const { error: insertError } = await supabase
        .from('provider_before_after_submissions')
        .insert({
          location_id: locationId,
          submitted_by: user.id,
          before_image_url: beforeUrl,
          after_image_url: afterUrl,
          treatment_type: baTreatmentType || null,
          description: baDescription || null,
        });

      if (insertError) throw insertError;

      // Notify admin via edge function
      await supabase.functions.invoke('send-content-approval-notification', {
        body: {
          type: 'before_after',
          locationId,
        },
      });

      toast({
        title: "Submitted for Approval",
        description: "Your before/after photos have been submitted. An admin will review them shortly.",
      });

      // Reset form
      setBeforeImage(null);
      setAfterImage(null);
      setBaDescription("");
      setBaTreatmentType("");
      queryClient.invalidateQueries({ queryKey: ['provider-ba-submissions'] });

    } catch (error: any) {
      console.error('Upload error:', error);
      toast({
        title: "Upload Failed",
        description: error.message || "Failed to upload images.",
        variant: "destructive",
      });
    } finally {
      setIsUploadingBA(false);
    }
  };

  const handleTestimonialSubmit = async () => {
    if (!clientName.trim() || !testimonialText.trim()) {
      toast({
        title: "Required Fields",
        description: "Please fill in client name and testimonial text.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmittingTestimonial(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase
        .from('provider_testimonial_submissions')
        .insert({
          location_id: locationId,
          submitted_by: user.id,
          client_name: clientName.trim(),
          testimonial_text: testimonialText.trim(),
          rating: parseInt(rating),
          treatment_type: testTreatmentType || null,
        });

      if (error) throw error;

      // Notify admin
      await supabase.functions.invoke('send-content-approval-notification', {
        body: {
          type: 'testimonial',
          locationId,
        },
      });

      toast({
        title: "Submitted for Approval",
        description: "Your testimonial has been submitted. An admin will review it shortly.",
      });

      // Reset form
      setClientName("");
      setTestimonialText("");
      setRating("5");
      setTestTreatmentType("");
      queryClient.invalidateQueries({ queryKey: ['provider-testimonial-submissions'] });

    } catch (error: any) {
      console.error('Submit error:', error);
      toast({
        title: "Submission Failed",
        description: error.message || "Failed to submit testimonial.",
        variant: "destructive",
      });
    } finally {
      setIsSubmittingTestimonial(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" />Approved</Badge>;
      case 'rejected':
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
      default:
        return <Badge variant="secondary"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Image className="w-5 h-5" />
          Content Submissions
        </CardTitle>
        <CardDescription>
          Submit before/after photos and testimonials for your location page. All submissions require admin approval.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="before-after" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="before-after">
              <Image className="w-4 h-4 mr-2" />
              Before/After Photos
            </TabsTrigger>
            <TabsTrigger value="testimonials">
              <MessageSquare className="w-4 h-4 mr-2" />
              Testimonials
            </TabsTrigger>
          </TabsList>

          <TabsContent value="before-after" className="space-y-6">
            {/* Upload Form */}
            <div className="border rounded-lg p-6 space-y-4">
              <h3 className="font-semibold">Add New Before/After</h3>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="before-image">Before Image</Label>
                  <Input
                    id="before-image"
                    type="file"
                    accept="image/*"
                    onChange={(e) => setBeforeImage(e.target.files?.[0] || null)}
                    className="mt-1"
                  />
                  {beforeImage && (
                    <p className="text-sm text-muted-foreground mt-1">{beforeImage.name}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="after-image">After Image</Label>
                  <Input
                    id="after-image"
                    type="file"
                    accept="image/*"
                    onChange={(e) => setAfterImage(e.target.files?.[0] || null)}
                    className="mt-1"
                  />
                  {afterImage && (
                    <p className="text-sm text-muted-foreground mt-1">{afterImage.name}</p>
                  )}
                </div>
              </div>

              <div>
                <Label htmlFor="ba-treatment">Treatment Type (optional)</Label>
                <Input
                  id="ba-treatment"
                  value={baTreatmentType}
                  onChange={(e) => setBaTreatmentType(e.target.value)}
                  placeholder="e.g., Botox, Filler, etc."
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="ba-description">Description (optional)</Label>
                <Textarea
                  id="ba-description"
                  value={baDescription}
                  onChange={(e) => setBaDescription(e.target.value)}
                  placeholder="Brief description of the treatment and results..."
                  className="mt-1"
                />
              </div>

              <Button onClick={handleBeforeAfterSubmit} disabled={isUploadingBA}>
                <Upload className="w-4 h-4 mr-2" />
                {isUploadingBA ? "Uploading..." : "Submit for Approval"}
              </Button>
            </div>

            {/* Existing Submissions */}
            {baSubmissions.length > 0 && (
              <div>
                <h3 className="font-semibold mb-4">Your Submissions</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {baSubmissions.map((submission: any) => (
                    <div key={submission.id} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        {getStatusBadge(submission.status)}
                        <span className="text-xs text-muted-foreground">
                          {new Date(submission.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <img 
                          src={submission.before_image_url} 
                          alt="Before" 
                          className="w-full aspect-square object-cover rounded"
                        />
                        <img 
                          src={submission.after_image_url} 
                          alt="After" 
                          className="w-full aspect-square object-cover rounded"
                        />
                      </div>
                      {submission.treatment_type && (
                        <p className="text-sm text-muted-foreground mt-2">{submission.treatment_type}</p>
                      )}
                      {submission.admin_notes && submission.status === 'rejected' && (
                        <p className="text-sm text-destructive mt-2">Note: {submission.admin_notes}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="testimonials" className="space-y-6">
            {/* Testimonial Form */}
            <div className="border rounded-lg p-6 space-y-4">
              <h3 className="font-semibold">Add New Testimonial</h3>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="client-name">Client Name</Label>
                  <Input
                    id="client-name"
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    placeholder="Jane D."
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="rating">Rating</Label>
                  <Select value={rating} onValueChange={setRating}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[5, 4, 3, 2, 1].map((r) => (
                        <SelectItem key={r} value={r.toString()}>
                          {r} Star{r !== 1 ? 's' : ''} {"★".repeat(r)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="test-treatment">Treatment Type (optional)</Label>
                <Input
                  id="test-treatment"
                  value={testTreatmentType}
                  onChange={(e) => setTestTreatmentType(e.target.value)}
                  placeholder="e.g., Botox, Filler, etc."
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="testimonial-text">Testimonial</Label>
                <Textarea
                  id="testimonial-text"
                  value={testimonialText}
                  onChange={(e) => setTestimonialText(e.target.value)}
                  placeholder="Client's testimonial..."
                  rows={4}
                  className="mt-1"
                />
              </div>

              <Button onClick={handleTestimonialSubmit} disabled={isSubmittingTestimonial}>
                <MessageSquare className="w-4 h-4 mr-2" />
                {isSubmittingTestimonial ? "Submitting..." : "Submit for Approval"}
              </Button>
            </div>

            {/* Existing Testimonials */}
            {testimonialSubmissions.length > 0 && (
              <div>
                <h3 className="font-semibold mb-4">Your Submissions</h3>
                <div className="space-y-4">
                  {testimonialSubmissions.map((submission: any) => (
                    <div key={submission.id} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{submission.client_name}</span>
                          <div className="flex items-center">
                            {Array.from({ length: submission.rating }).map((_, i) => (
                              <Star key={i} className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                            ))}
                          </div>
                        </div>
                        {getStatusBadge(submission.status)}
                      </div>
                      <p className="text-muted-foreground text-sm">{submission.testimonial_text}</p>
                      {submission.admin_notes && submission.status === 'rejected' && (
                        <p className="text-sm text-destructive mt-2">Note: {submission.admin_notes}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};
